import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import "./Input.css";

// ─── Filtres de saisie ────────────────────────────────────────────────────────
// Caractères interdits selon le type de champ
const FILTER_STRIP = {
  letters:    /[^a-zA-ZÀ-ÿ\s\-'.]/g,   // nom, prénom, ville, région
  digits:     /[^\d]/g,                  // code postal, numéros purs
  phone:      /[^\d\s\-().+]/g,          // téléphone
};

const FILTER_MAXLEN = {
  phone: 20,   // ex: +225 07 12 34 56 78  = 18 chars
  digits: 10,
};


const applyFilter = (value, filter) => {
  const pattern = FILTER_STRIP[filter];
  let result = pattern ? value.replace(pattern, '') : value;
  const maxLen = FILTER_MAXLEN[filter];
  if (maxLen && result.length > maxLen) result = result.slice(0, maxLen);
  return result;
};

// Calcule la force d'un mot de passe (0-4)
const getPasswordStrength = (pwd) => {
  if (!pwd) return 0;
  let score = 0;
  if (pwd.length >= 8)              score++;
  if (/[A-Z]/.test(pwd))           score++;
  if (/[a-z]/.test(pwd))           score++;
  if (/\d/.test(pwd))              score++;
  if (/[^A-Za-z0-9]/.test(pwd))   score++;
  return score;
};

const STRENGTH_CLASSES = ['', 'strength-very-weak', 'strength-weak', 'strength-medium', 'strength-strong', 'strength-very-strong'];
const STRENGTH_KEYS   = ['', 'veryWeak', 'weak', 'medium', 'strong', 'veryStrong'];

const Input = ({
  label,
  type = "text",
  name,
  value,
  onChange,
  onBlur,
  placeholder,
  required = false,
  min,
  max,
  error,
  valid,
  disabled,
  helpText,
  showStrength = false,
  filter,
  ...props
}) => {
  const { t } = useTranslation('common');
  const [showPassword, setShowPassword] = useState(false);

  // Intercepte onChange pour supprimer les caractères interdits avant de propager
  const handleChange = (e) => {
    if (!onChange) return;
    if (!filter) return onChange(e);
    const cleaned = applyFilter(e.target.value, filter);
    if (cleaned === e.target.value) return onChange(e);
    // Passe un objet minimaliste { target: { name, value } } - compatible avec tous les handlers
    onChange({ target: { name: e.target.name, value: cleaned } });
  };
  const isPassword = type === "password";
  const inputType = isPassword && showPassword ? "text" : type;

  const strength = isPassword && showStrength ? getPasswordStrength(value) : 0;

  return (
    <div className="form-group">
      {label && (
        <label htmlFor={name}>
          {/* P2 — marquage obligatoire UNIFIÉ : on retire un éventuel « * » déjà présent dans le
              libellé et on affiche UN SEUL astérisque stylé si le champ est requis OU si le
              libellé le portait. Évite les doubles astérisques et harmonise le style. */}
          {typeof label === 'string' ? label.replace(/\s*\*\s*$/, '') : label}
          {(required || (typeof label === 'string' && /\*\s*$/.test(label))) && <span className="required"> *</span>}
        </label>
      )}

      <div className={`input-wrapper${isPassword ? ' input-wrapper--password' : ''}${type === 'date' ? ' input-wrapper--date' : ''}`}>
        <input
          type={inputType}
          id={name}
          name={name}
          value={value || ""}
          onChange={handleChange}
          onBlur={onBlur}
          placeholder={placeholder}
          required={required}
          min={min}
          max={max}
          maxLength={filter ? FILTER_MAXLEN[filter] : undefined}
          disabled={disabled}
          className={`form-input ${error ? "error" : valid ? "valid" : ""} ${disabled ? "disabled" : ""}`}
          {...props}
        />
        {/* Hint visible quand le champ date est vide — compense l'absence de placeholder sur iOS */}
        {type === 'date' && !value && (
          <span className="date-hint" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
              <line x1="16" y1="2" x2="16" y2="6"/>
              <line x1="8" y1="2" x2="8" y2="6"/>
              <line x1="3" y1="10" x2="21" y2="10"/>
            </svg>
            {t('ui.dateFormat')}
          </span>
        )}
        {isPassword && (
          <button
            type="button"
            className="password-toggle"
            onClick={() => setShowPassword(v => !v)}
            tabIndex={-1}
            aria-label={showPassword ? t('input.hidePassword') : t('input.showPassword')}
          >
            {showPassword ? (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94"/>
                <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19"/>
                <line x1="1" y1="1" x2="23" y2="23"/>
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                <circle cx="12" cy="12" r="3"/>
              </svg>
            )}
          </button>
        )}
      </div>

      {isPassword && showStrength && value && (
        <div className="password-strength">
          <div className="strength-bar">
            {[1,2,3,4,5].map(i => (
              <div key={i} className={`strength-segment ${i <= strength ? STRENGTH_CLASSES[strength] : ''}`} />
            ))}
          </div>
          {strength > 0 && (
            <span className={`strength-label ${STRENGTH_CLASSES[strength]}`}>
              {t(`input.strength.${STRENGTH_KEYS[strength]}`)}
            </span>
          )}
        </div>
      )}

      {error && <div className="error-message">{error}</div>}
      {!error && (helpText || filter) && (
        <div className="help-text">{helpText || t(`input.filterHint.${filter}`)}</div>
      )}
    </div>
  );
};

export default Input;
